import type {
  CardsResponse,
  DiagnosisResultResponse,
  CardResponse,
  DiagnosisCard,
} from '@/apis/repositories/models/derek'
import type { DiagnosisSummary, DiagnosisAnalysis, Rank } from '@/types/app'
import { formatDiagnosisType } from '@/libs/utilities/formatDiagnosisResult'
import type { DiagnosisByUserDetailFragment } from '@/pages/ChatPage/ChatPageOwnView/hooks/useOwnChatRoom.generated.ts'
import type { RankInCluster } from '@/types/schema.generated.ts'
import type { DiagnosisByKarteQuery } from '@/pages/KarteListPage/hooks/useKarteUserDetail.generated.ts'

const parseAnalysisItem = <U extends keyof DiagnosisAnalysis>({
  rank,
  myValue,
  ...rest
}: CardsResponse[keyof CardsResponse]): DiagnosisAnalysis[U] =>
  ({
    ...rest,
    userValue: myValue,
    rank: rank ?? undefined,
  }) as unknown as DiagnosisAnalysis[U]

const convertDiagnosisCardToCardResponse = <
  RankType extends Rank | null,
  UnitType extends '万円' | '円',
>(
  card: DiagnosisCard,
  unit: UnitType,
  rank: RankType
): CardResponse<RankType, UnitType> => {
  return {
    name: card.name,
    title: card.title,
    rank: rank,
    myValue: card.myValue,
    averageValue: card.averageValue,
    unit: unit,
    message: card.message,
    showBarGraph: card.showBarGraph,
  }
}

const convertClusterRankToRank = (
  rank: RankInCluster | null | undefined
): Rank | null => {
  if (!rank || rank === 'UNSPECIFIED') {
    return null
  }
  return rank as Rank
}

export const parseDiagnosisByUserResponseToDiagnosisSummary = (
  diagnosisByUserDetail: DiagnosisByUserDetailFragment
): DiagnosisSummary => {
  const { personality, cards } = diagnosisByUserDetail

  return {
    analysis: {
      annualIncome: parseAnalysisItem<'annualIncome'>(
        convertDiagnosisCardToCardResponse(
          cards.annualIncome,
          '万円',
          convertClusterRankToRank(cards.annualIncome.rank)
        )
      ),
      annualSaving: parseAnalysisItem<'annualSaving'>(
        convertDiagnosisCardToCardResponse(
          cards.savingAnnual,
          '万円',
          convertClusterRankToRank(cards.savingAnnual.rank)
        )
      ),
      monthlyRentCost: parseAnalysisItem<'monthlyRentCost'>(
        convertDiagnosisCardToCardResponse(
          cards.monthlyRentCost,
          '万円',
          convertClusterRankToRank(cards.monthlyRentCost.rank)
        )
      ),
      investment: parseAnalysisItem<'investment'>(
        convertDiagnosisCardToCardResponse(cards.investment, '万円', null)
      ),
      monthlyInsurance: parseAnalysisItem<'monthlyInsurance'>(
        convertDiagnosisCardToCardResponse(cards.monthlyInsurance, '円', null)
      ),
    },
    result: {
      personality: {
        type: personality.type,
        title: personality.title,
        description: personality.description,
      },
    },
  }
}

export const parseDiagnosisByKarteResponseToDiagnosisSummary = (
  diagnosisByKarteQuery: DiagnosisByKarteQuery
): DiagnosisSummary => {
  const { diagnosisByKarte } = diagnosisByKarteQuery

  return {
    analysis: {
      annualIncome: parseAnalysisItem<'annualIncome'>(
        convertDiagnosisCardToCardResponse(
          diagnosisByKarte.cards.annualIncome,
          '万円',
          convertClusterRankToRank(diagnosisByKarte.cards.annualIncome.rank)
        )
      ),
      annualSaving: parseAnalysisItem<'annualSaving'>(
        convertDiagnosisCardToCardResponse(
          diagnosisByKarte.cards.savingAnnual,
          '万円',
          convertClusterRankToRank(diagnosisByKarte.cards.savingAnnual.rank)
        )
      ),
      monthlyRentCost: parseAnalysisItem<'monthlyRentCost'>(
        convertDiagnosisCardToCardResponse(
          diagnosisByKarte.cards.monthlyRentCost,
          '万円',
          convertClusterRankToRank(diagnosisByKarte.cards.monthlyRentCost.rank)
        )
      ),
      investment: parseAnalysisItem<'investment'>(
        convertDiagnosisCardToCardResponse(
          diagnosisByKarte.cards.investment,
          '万円',
          null
        )
      ),
      monthlyInsurance: parseAnalysisItem<'monthlyInsurance'>(
        convertDiagnosisCardToCardResponse(
          diagnosisByKarte.cards.monthlyInsurance,
          '円',
          null
        )
      ),
    },
    result: {
      personality: {
        type: diagnosisByKarte.personality.type,
        title: diagnosisByKarte.personality.title,
        description: diagnosisByKarte.personality.description,
      },
    },
  }
}

export const createDiagnosisSummary = ({
  personality,
  cards,
}: DiagnosisResultResponse): DiagnosisSummary => ({
  analysis: {
    annualIncome: parseAnalysisItem<'annualIncome'>(cards.annual_income),
    annualSaving: parseAnalysisItem<'annualSaving'>(cards.saving_annual),
    monthlyRentCost: parseAnalysisItem<'monthlyRentCost'>(
      cards.monthly_rent_cost
    ),
    investment: parseAnalysisItem<'investment'>(cards.investment),
    monthlyInsurance: parseAnalysisItem<'monthlyInsurance'>(
      cards.monthly_insurance
    ),
  },
  result: {
    personality: {
      type: formatDiagnosisType(personality.id),
      title: personality.title,
      description: personality.description,
    },
  },
})
